import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '../stores/auth.js'

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior: () => ({ top: 0 }),
  routes: [
    {
      path: '/welcome',
      component: () => import('../views/LandingView.vue'),
    },
    {
      // /uk → UK welcome screen (the app)
      path: '/uk',
      redirect: '/welcome',
    },
    {
      // /de → German welcome screen (future)
      path: '/de',
      redirect: '/welcome',  // placeholder until German app is built
      meta: { guestOnly: true },
    },
    {
      path: '/',
      component: () => import('../views/HomeView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/login',
      component: () => import('../views/LoginView.vue'),
      meta: { guestOnly: true },
    },
    {
      path: '/auth/verify',
      component: () => import('../views/AuthVerifyView.vue'),
    },
    {
      path: '/upload',
      component: () => import('../views/UploadView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/tickets/:id',
      component: () => import('../views/TicketView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/tickets/:id/appeal',
      component: () => import('../views/AppealView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/tickets/:id/payment',
      component: () => import('../views/PaymentView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/tickets/:id/submitted',
      component: () => import('../views/SubmittedView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/tickets/:id/referral',
      component: () => import('../views/ReferralView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/tickets/:id/popla',
      component: () => import('../views/PopolaView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/tickets/:id/speeding-assessment',
      component: () => import('../views/SpeedingAssessmentView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/appeals/:id',
      component: () => import('../views/AppealDetailView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/history',
      component: () => import('../views/HistoryView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/account',
      component: () => import('../views/AccountView.vue'),
      meta: { requiresAuth: true },
    },
    // ── Fine type routes ──────────────────────────────────────────────────────
    {
      path: '/challenge',
      component: () => import('../views/UnifiedChallengeView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/fines',
      component: () => import('../views/FineTypeSelectView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/fines/:type/analyse',
      component: () => import('../views/FineAnalyseView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/fines/:type/letter',
      component: () => import('../views/FineLetterView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/fines/:type/referral',
      component: () => import('../views/ReferralView.vue'),
      meta: { requiresAuth: true },
    },
        {
      path: '/privacy',
      component: () => import('../views/PrivacyView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/terms',
      component: () => import('../views/TermsView.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/faq',
      component: () => import('../views/FaqView.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/:pathMatch(.*)*',
      component: () => import('../views/NotFoundView.vue'),
    },
  ],
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  if (!auth.initialised) await auth.init()

  // ── Terms & Conditions gate ───────────────────────────────────────────────
  // Show T&Cs only when terms_version has CHANGED (or user has never agreed).
  // Never show on every login — only on first use or version bump.
  const CURRENT_TERMS = '2026-04'
  const publicRoutes  = ['/welcome', '/login', '/terms', '/privacy', '/auth/verify']
  const isPublic      = publicRoutes.some(p => to.path.startsWith(p))

  if (auth.isLoggedIn && !isPublic) {
    const u = auth.user as any
    // Fetch fresh data if user object is partially populated
    if (u && u.terms_version === undefined && u.id) {
      await auth.fetchMe()
    }
    // null means never agreed, undefined means not loaded yet, string means agreed to that version
    const termsVersion = (auth.user as any)?.terms_version
    const agreed = termsVersion === CURRENT_TERMS
    if (!agreed) {
      return { path: '/terms', query: { agree: '1' } }
    }
  }

  if (to.meta.requiresAuth && !auth.isLoggedIn) {
    // First-time visitors see the landing page, not the login form
    if (to.path === '/') return { path: '/welcome' }
    return { path: '/login', query: { redirect: to.fullPath } }
  }
  if (to.meta.guestOnly && auth.isLoggedIn) {
    return { path: '/' }
  }
})

export default router
